import React, { useEffect, useRef, useState } from 'react';
import type { PortalBootstrapData } from './types/window';
import { __ } from '@/hub-shared/utils/i18n';
import { loadVsmeBundle } from '@/hub-shared/utils/loadVsmeBundle';
import FeatureGateNotice from '@/hub-shared/components/FeatureGateNotice';
import BreadcrumbBar from '@tpct/mission-core/components/BreadcrumbBar';
import { LoadingState } from '@tpct/admin-ui';
import { fetchWithNonce } from './utils/fetchWithNonce';
import { parseApiError } from './utils/parseApiError';
import { errorToUserFacingMessage } from './api/apiErrors';
import { getPortalGate } from './gates/portalGates';
import { vsmeMissionsListPath, parseDiligenceViewStateFromSearch } from '@tpct/mission-core/utils/vsmePortalRoutes';

type VsmeMode = 'dashboard' | 'reports' | 'diligence' | 'dossier' | 'settings';

type VsmePortalPageProps = {
  bootstrap: PortalBootstrapData;
  mode: VsmeMode;
  reportId?: number;
  missionId?: number;
  /** Portal location search (`?cycle=…`) so dossier deep-links survive async bootstrap. */
  locationSearch?: string;
};

type VsmeApi = { mount: (container: HTMLElement) => void; unmount: () => void };

const DOSSIER_LOADING_COLUMNS = [
  __('Cycle'),
  __('Complétion formulaire'),
  __('Diligences'),
  __('Actions'),
];

const VsmePortalPage = ({ bootstrap, mode, reportId, missionId, locationSearch }: VsmePortalPageProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const loadedRef = useRef<string | null>(null);
  const [diligenceError, setDiligenceError] = useState<string | null>(null);
  const [resolvedReportId, setResolvedReportId] = useState<number | null>(reportId ?? null);
  // Diligence is a runtime-injected micro-frontend (like dashboard/reports): fetch its
  // bootstrap here, publish it to window.VSMEDiligenceAdmin, then load + mount the vsme
  // diligence bundle via window.VSMEDiligenceAPI.mount(container).
  const [diligenceBootstrap, setDiligenceBootstrap] = useState<Record<string, unknown> | null>(null);
  const [diligenceLoading, setDiligenceLoading] = useState(
    () => mode === 'dossier' || mode === 'diligence',
  );

  const vsme = bootstrap.vsme;
  const hasDashboardBundle = Boolean(vsme?.dashboard && vsme?.assetUrls?.dashboard);
  const hasReportsBundle = Boolean(vsme?.reports && vsme?.assetUrls?.reports);
  const hasDiligenceBundle = Boolean(vsme?.assetUrls?.diligence);
  const vsmeAccessGate = getPortalGate(bootstrap, 'vsme.access');
  const portalBase = bootstrap.portalPath.replace(/\/$/, '');

  const dossierBreadcrumb = (
    <BreadcrumbBar
      items={[
        {
          label: __('Missions VS'),
          onClick: () => {
            window.location.assign(vsmeMissionsListPath(portalBase));
          },
        },
        { label: __('Dossier de travail') },
      ]}
    />
  );

  useEffect(() => {
    if (mode !== 'dashboard' || hasDashboardBundle) {
      return;
    }
    if (hasReportsBundle) {
      window.location.replace(vsmeMissionsListPath(portalBase));
    }
  }, [hasDashboardBundle, hasReportsBundle, mode, portalBase]);

  const activeReportId = mode === 'dossier' ? resolvedReportId : reportId ?? null;

  const modeReady =
    mode === 'settings'
    || (mode === 'dashboard' && hasDashboardBundle)
    || (mode === 'reports' && hasReportsBundle)
    || (mode === 'diligence' && hasDiligenceBundle && activeReportId !== null)
    || (mode === 'dossier' && hasDiligenceBundle && missionId !== undefined && missionId > 0);

  // Dashboard/reports remain runtime-injected micro-frontends (separate PR to migrate).
  useEffect(() => {
    if (!modeReady || mode === 'settings') return;
    if (mode !== 'dashboard' && mode !== 'reports') return;

    const dashboardConfig = vsme!.dashboard as Record<string, unknown>;
    const reportsConfig = vsme!.reports as Record<string, unknown>;
    const assetUrls = vsme!.assetUrls!;

    let cancelled = false;
    const run = async () => {
      try {
        if (mode === 'dashboard') {
          (window as unknown as Record<string, unknown>).VSMEDashboardAdmin = dashboardConfig;
          const src = assetUrls.dashboard;
          if (src && loadedRef.current !== 'dashboard') {
            await loadVsmeBundle('dashboard', src, 'VSMEDashboardAPI', __('Impossible de charger le module VS.'));
            loadedRef.current = 'dashboard';
          }
          if (cancelled) return;
          const api = (window as unknown as Record<string, VsmeApi>).VSMEDashboardAPI;
          const container = containerRef.current;
          if (api?.mount && container) api.mount(container);
        } else if (mode === 'reports') {
          (window as unknown as Record<string, unknown>).VSMEReportsAdmin = reportsConfig;
          const src = assetUrls.reports;
          if (src && loadedRef.current !== 'reports') {
            await loadVsmeBundle('reports', src, 'VSMEReportsAPI', __('Impossible de charger le module VS.'));
            loadedRef.current = 'reports';
          }
          if (cancelled) return;
          const api = (window as unknown as Record<string, VsmeApi>).VSMEReportsAPI;
          const container = containerRef.current;
          if (api?.mount && container) api.mount(container);
        }
      } catch (err) {
        console.error('VSME script load error:', err);
      }
    };

    void run();

    return () => {
      cancelled = true;
      if (mode === 'dashboard') {
        (window as unknown as Record<string, VsmeApi>).VSMEDashboardAPI?.unmount?.();
      } else if (mode === 'reports') {
        (window as unknown as Record<string, VsmeApi>).VSMEReportsAPI?.unmount?.();
      }
    };
  }, [bootstrap.nonce, bootstrap.restUrls.vsme, mode, modeReady, vsme]);

  // Diligence/dossier: fetch the bootstrap the diligence bundle needs. Dossier resolves the
  // linked report from the mission in a single call (mission-keyed bootstrap), so no separate
  // workfile-context round-trip is needed. The mount effect below injects + mounts the bundle.
  useEffect(() => {
    if (mode !== 'diligence' && mode !== 'dossier') return;
    if (mode === 'diligence' && ((reportId ?? 0) <= 0)) return;
    if (mode === 'dossier' && ((missionId ?? 0) <= 0)) return;

    let cancelled = false;
    const run = async () => {
      setDiligenceLoading(true);
      setDiligenceError(null);
      setDiligenceBootstrap(null);
      try {
        const url =
          mode === 'dossier'
            ? `${bootstrap.restUrls.vsme}missions/${missionId}/diligences/bootstrap`
            : `${bootstrap.restUrls.vsme}reports/${reportId}/diligences/bootstrap`;
        const res = await fetchWithNonce(url, {
          headers: { 'X-WP-Nonce': bootstrap.nonce },
          credentials: 'same-origin',
        });
        if (!res.ok) {
          if (!cancelled) setDiligenceError(await parseApiError(res));
          return;
        }
        const json = (await res.json()) as { success?: boolean; data?: Record<string, unknown> };
        const serverBootstrap = (json.data ?? json) as Record<string, unknown>;
        const bootstrapReportId = Number(serverBootstrap.reportId) || (reportId ?? 0);
        const dossierViewState = parseDiligenceViewStateFromSearch(
          locationSearch ?? window.location.search,
        );
        const config = {
          ...serverBootstrap,
          ...(vsme!.diligence as Record<string, unknown>),
          reportId: bootstrapReportId,
          missionId,
          currentCycle: dossierViewState.cycle,
          statusFilter: dossierViewState.status,
          selectedDiligenceId: dossierViewState.diligenceId,
        } as Record<string, unknown>;
        // The diligence bundle reads its config from window.VSMEDiligenceAdmin at mount();
        // the mount effect publishes it there once this fetch resolves.
        if (!cancelled) {
          setResolvedReportId(bootstrapReportId > 0 ? bootstrapReportId : null);
          setDiligenceBootstrap(config);
        }
      } catch (err) {
        if (!cancelled) {
          setDiligenceError(errorToUserFacingMessage(err, __('Impossible de charger le module diligence.')));
        }
      } finally {
        if (!cancelled) setDiligenceLoading(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [bootstrap.nonce, bootstrap.restUrls.vsme, locationSearch, missionId, mode, reportId, vsme]);

  // Runtime-mount the diligence bundle once its bootstrap is resolved and the container
  // is in the DOM. Publishes config to window.VSMEDiligenceAdmin (the bundle reads it),
  // loads the vsme diligence script, then mounts into the container; unmounts on cleanup.
  useEffect(() => {
    if (mode !== 'diligence' && mode !== 'dossier') return;
    if (diligenceBootstrap === null) return;
    const container = containerRef.current;
    const src = vsme?.assetUrls?.diligence;
    if (!container || !src) return;

    let cancelled = false;
    (window as unknown as Record<string, unknown>).VSMEDiligenceAdmin = diligenceBootstrap;
    const run = async () => {
      try {
        await loadVsmeBundle('diligence', src, 'VSMEDiligenceAPI', __('Impossible de charger le module VS.'));
        if (cancelled) return;
        const api = (window as unknown as Record<string, VsmeApi>).VSMEDiligenceAPI;
        if (api?.mount) api.mount(container);
      } catch (err) {
        if (!cancelled) {
          setDiligenceError(errorToUserFacingMessage(err, __('Impossible de charger le module diligence.')));
        }
      }
    };
    void run();

    return () => {
      cancelled = true;
      (window as unknown as Record<string, VsmeApi>).VSMEDiligenceAPI?.unmount?.();
    };
  }, [diligenceBootstrap, mode, vsme]);

  if (!vsmeAccessGate.allowed) {
    return <FeatureGateNotice decision={vsmeAccessGate} />;
  }

  if (mode === 'settings') {
    return (
      <div>
        <h1 className="wp-heading-inline">{__('Réglages VS')}</h1>
        <p>{__('Les réglages avancés sont en cours de migration vers le portail.')}</p>
      </div>
    );
  }

  if (!modeReady) {
    const missingDashboardOnly = mode === 'dashboard' && hasReportsBundle;
    return (
      <div>
        <h1 className="wp-heading-inline">{__('VS (vsme)')}</h1>
        <div className="accounting-hub-notice" role="alert">
          <p>
            {missingDashboardOnly
              ? __('Le tableau de bord VS est désactivé. Utilisez la liste des missions VS.')
              : __("VS n'est pas disponible. Activez le module VS pour accéder à cette section.")}
          </p>
          {missingDashboardOnly ? (
            <p>
              <a href={vsmeMissionsListPath(portalBase)}>{__('Ouvrir Missions VS')}</a>
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  if (
    (mode === 'diligence' || mode === 'dossier')
    && !diligenceLoading
    && !diligenceError
    && (activeReportId === null || activeReportId <= 0)
  ) {
    return (
      <div className="mission-page">
        {dossierBreadcrumb}
        <h1 className="wp-heading-inline">{__('Dossier de travail')}</h1>
        <div className="accounting-hub-notice" role="alert">
          <p>{diligenceError ?? __('Rapport introuvable.')}</p>
        </div>
      </div>
    );
  }

  if ((mode === 'diligence' || mode === 'dossier') && diligenceError) {
    return (
      <div className="mission-page">
        {dossierBreadcrumb}
        <h1 className="wp-heading-inline">{__('Dossier de travail')}</h1>
        <div className="accounting-hub-notice" role="alert">
          <p>{diligenceError}</p>
        </div>
      </div>
    );
  }

  if (mode === 'diligence' || mode === 'dossier') {
    if (diligenceLoading || diligenceBootstrap === null) {
      return (
        <div className="mission-page">
          {dossierBreadcrumb}
          <section className="container-style mission-page__card mission-page__card--span-5" aria-labelledby="dossier-mount-loading-heading">
            <h1 id="dossier-mount-loading-heading" className="wp-heading-inline">{__('Dossier de travail')}</h1>
            <LoadingState
              columns={DOSSIER_LOADING_COLUMNS}
              rowCount={4}
              className="mission-cycles-loading"
            />
          </section>
        </div>
      );
    }

    return <div ref={containerRef} id="vsme-diligence-container" style={{ minHeight: 200 }} />;
  }

  const containerId = mode === 'dashboard' ? 'vsme-dashboard-container' : 'vsme-reports-container';

  return <div ref={containerRef} id={containerId} style={{ minHeight: 200 }} />;
};

export default VsmePortalPage;
